import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { useIntl, FormattedMessage, defineMessages } from '@edx/frontend-platform/i18n';
import { Icon, Container, ActionRow, Button, ButtonGroup, TransitionReplace } from '@openedx/paragon';
import { Nightlight, WbSunny, ExpandLess, ExpandMore, Help } from '@openedx/paragon/icons';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeCookie = 'hyper-toggle-dark';
let themeCookieExpiry = 90; // days

const AddDarkTheme = () => {
  const cookies = new Cookies();
  const isThemeToggleEnabled = getConfig().HYPER_ENABLE_DARK_TOGGLE;

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + themeCookieExpiry);
  };

  const getCookieOptions = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const options = { domain: serverURL.hostname, path: '/', expires: getCookieExpiry() };
    return options;
  };

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[index].contentDocument) { iframes[index].contentDocument.head.appendChild(style); }
      });
    }
  };

  useEffect(() => {
    const theme = cookies.get(themeCookie);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.body.classList.add('indigo-dark-theme');
      
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage

      cookies.set(themeCookie, theme, getCookieOptions());      //  on page load, update expiry
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
// Brand guideline: maintain clear space "X" around the logo (~13% of height)
const logoStyle = { maxHeight: '45px', margin: '6px' };
const mobileLogoStyle = { margin: '3px' };

const HyperFooter = () => {
  const config = getConfig();

  return (
    <footer role="contentinfo" className="footer d-flex border-top py-3 px-4">
      <div className="container-fluid d-flex">
        <a className="d-block" href={config.LMS_BASE_URL} aria-label="HYPER Academy Home">
          <img
            src={`${config.LMS_BASE_URL}/theming/asset/images/logo.png`}
            alt="Powered by Happy Youth Pathways Education for Rural Villages"
            style={logoStyle}
          />
        </a>
      </div>
    </footer>
  );
};

const studioFooterHelpMessages = defineMessages({
  openHelpButtonLabel: {
    id: 'authoring.footer.help.openHelp.button.label',
    defaultMessage: 'Looking for help with Studio?',
    description: 'Label for button that opens the collapsed section with help buttons',
  },
  closeHelpButtonLabel: {
    id: 'authoring.footer.help.closeHelp.button.label',
    defaultMessage: 'Hide Studio help',
    description: 'Label for button that closes the collapsed section with help buttons',
  },
  educatorsDocsButtonLabel: {
    id: 'authoring.footer.help.educatorsDocs.button.label',
    defaultMessage: 'Open edX Educators Docs',
    description: 'Label for button that links to the build a course quickstart site',
  },
  openEdxDemoCourseButtonLabel: {
    id: 'authoring.footer.help.openEdxDemoCourse.button.label',
    defaultMessage: 'Open edX Demo Course',
    description: 'Label for button that links to the open edX demo course portal',
  },
  contactUsButtonLabel: {
    id: 'authoring.footer.help.contactUs.button.label',
    defaultMessage: 'Contact us',
    description: 'Label for button that links to the email for partner support',
  },
});

const HyperStudioFooterHelpSection = ({ containerProps }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const intl = useIntl();
  const { containerClassName, ...restContainerProps } = containerProps || {};

  const helpButtons = [
    {
      as: 'a',
      href: 'https://docs.openedx.org/en/latest/educators/quickstarts/build_a_course.html',
      size: 'sm',
      message: studioFooterHelpMessages.educatorsDocsButtonLabel,
      dataTestid: null,
    },
    {
      as: 'a',
      href: 'https://training.openedx.io/courses/course-v1:OpenedX+DemoX+Demo_Course/about',
      size: 'sm',
      message: studioFooterHelpMessages.openEdxDemoCourseButtonLabel,
      dataTestid: 'openEdxDemoCourseButton',
    },
  ];

  return (
    <>
      <div className="m-0 mt-6 row align-items-center justify-content-center">
        <div className="col border-top mr-2" />
        <Button
          data-testid="helpToggleButton"
          variant="outline-primary"
          onClick={() => setIsOpen(!isOpen)}
          iconBefore={Help}
          iconAfter={isOpen ? ExpandLess : ExpandMore}
          size="sm"
        >
          {isOpen ? intl.formatMessage(studioFooterHelpMessages.closeHelpButtonLabel)
            : intl.formatMessage(studioFooterHelpMessages.openHelpButtonLabel)}
        </Button>
        <div className="col border-top ml-2" />
      </div>
      <Container
        size="xl"
        className={classNames('px-4', containerClassName)}
        {...restContainerProps}
      >
        {isOpen && (
          <TransitionReplace>
            <ActionRow key="help-link-button-row" className="py-4" data-testid="helpButtonRow">
              <ActionRow.Spacer />
              {helpButtons.map(({ as, href, size, message, dataTestid }) => (
                <Button as={as} href={href} size={size} key={message.id} data-testid={dataTestid}>
                  <FormattedMessage {...message} />
                </Button>
              ))}
              {!isEmpty(getConfig().SUPPORT_EMAIL) && (
                <Button
                  as="a"
                  href={`mailto:${getConfig().SUPPORT_EMAIL}`}
                  size="sm"
                  data-testid="contactUsButton"
                >
                  <FormattedMessage {...studioFooterHelpMessages.contactUsButtonLabel} />
                </Button>
              )}
              <ActionRow.Spacer />
            </ActionRow>
          </TransitionReplace>
        )}
      </Container>
    </>
  );
};

const HyperStudioFooter = (containerProps) => {
  const config = getConfig();

  return (
    <>
      <HyperStudioFooterHelpSection containerProps={containerProps} />
      <Container size="xl" className="px-4">
        <div className="pt-3 pb-4">
          <a className="d-block" href={config.LMS_BASE_URL} aria-label="HYPER Academy Home">
            <img
              src={`${config.LMS_BASE_URL}/theming/asset/images/logo.png`}
              alt="Powered by Happy Youth Pathways Education for Rural Villages"
              style={logoStyle}
            />
          </a>
        </div>
      </Container>
    </>
  );
};

const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = "hyper-toggle-dark";
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().HYPER_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: "/",
    expires: getCookieExpiry(),
  });

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName("iframe");
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, ind) => {
        const style = document.createElement("style");
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[ind].contentDocument) {
          iframes[ind].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  const removeDarkThemeFromiframes = () => {
    const iframes = document.getElementsByTagName("iframe");
    const iframesLength = iframes.length;

    Array.from({ length: iframesLength }).forEach((_, ind) => {
      if (iframes[ind].contentDocument) {
        const iframeHead = iframes[ind].contentDocument.head;
        const styleTag = Array.from(iframeHead.querySelectorAll("style")).find(
          (style) =>
            style.textContent.includes("background-color: #0D0D0E;") &&
            style.textContent.includes("color: #ccc;")
        );
        if (styleTag) {
          styleTag.remove();
        }
      }
    });
  };

  const onToggleTheme = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    let theme = "";

    if (getCookie(themeCookie) === "dark") {
      document.body.classList.remove("indigo-dark-theme");
      removeDarkThemeFromiframes();
      setIsDarkThemeEnabled(false);
      theme = "light";
    } else {
      document.body.classList.add("indigo-dark-theme");
      addDarkThemeToIframes();
      setIsDarkThemeEnabled(true);
      theme = "dark";
    }
    setCookie(themeCookie, theme, getCookieOptions(serverURL));

    const learningMFEUnitIframe = document.getElementById("unit-iframe");
    if (learningMFEUnitIframe) {
      learningMFEUnitIframe.contentWindow.postMessage(
        { "hyper-toggle-dark": theme },
        serverURL.origin
      );
    }
  };

  const hanldeKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={hanldeKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${
            isDarkThemeEnabled ? "Light" : "Dark"
          } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};

const CatalogHomeOverlay = () => (
  <>
    {/* frontend-app-catalog exposes --catalog-home-page-banner-background-{color,image}
        as the brand override hook (default is --pgn-color-gray-500). */}
    <style>{`
      :root {
        --catalog-home-page-banner-background-color: #509310;
        --catalog-home-page-banner-background-image: linear-gradient(135deg, #A4C63A 0%, #509310 100%);
      }
    `}</style>
    <h1 className="display-1 text-white text-center">
      Welcome to Open Sikhya By Hyper Academy
    </h1>
  </>
);

const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "Mobile view header logo altText",
    },
  };
  return (
    <div className="container-xl py-2 d-flex align-items-center justify-content-between">
      <a
        className="logo"
        href={`${config.LMS_BASE_URL}/dashboard`}
        style={Object.assign({}, { display: "flex", alignItems: "center" })}
      >
        <img
          className="d-block"
          src={`${config.LMS_BASE_URL}/theming/asset/images/logo.png`}
          alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])}
          height={21}
          style={mobileLogoStyle}
        />
      </a>
      <ToggleThemeButton />
    </div>
  );
};

const parentDashboardMessages = defineMessages({
  goToParentDashboard: {
    id: 'learnerVariantDashboard.menu.parentDashboard.label',
    defaultMessage: 'Go to Parent Dashboard',
    description: 'Header link for navigating to the Parent Dashboard.',
  },
});

const useIsParent = () => {
  const [isParent, setIsParent] = React.useState(false);

  React.useEffect(() => {
    const parentDashboardUrl = getConfig().PARENT_DASHBOARD_URL;
    if (!parentDashboardUrl) {
      return;
    }

    const checkParentStatus = async () => {
      try {
        const { data } = await getAuthenticatedHttpClient()
          .get(`${getConfig().LMS_BASE_URL}/parentapi/is-parent/`);
        setIsParent(data.is_parent || false);
      } catch (e) {
        setIsParent(false);
      }
    };

    checkParentStatus();
  }, []);

  return isParent;
};

const ParentDashboardLink = () => {
  const isParent = useIsParent();
  const parentDashboardUrl = getConfig().PARENT_DASHBOARD_URL;
  if (!isParent || !parentDashboardUrl) {
    return null;
  }
  return (
    <Button variant="outline-primary" className="mr-2 border-0" href={parentDashboardUrl}>
      <FormattedMessage {...parentDashboardMessages.goToParentDashboard} />
    </Button>
  );
};

const roleRedirectLabelStyle = { fontWeight: 500 };

const RoleRedirectWidget = () => {
  const getInitialRole = () => {
    const params = new URLSearchParams(window.location.search);
    const next = params.get('next');
    const studioUrl = getConfig().STUDIO_BASE_URL;
    const parentDashboardUrl = getConfig().PARENT_DASHBOARD_URL;
    if (next && studioUrl && next === studioUrl) return 'instructor';
    if (next && parentDashboardUrl && next === parentDashboardUrl) return 'parent';
    return 'learner';
  };

  const [selectedRole, setSelectedRole] = useState(getInitialRole);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    const url = new URL(window.location.href);

    if (role === 'instructor') {
      url.searchParams.set('next', getConfig().STUDIO_BASE_URL);
    } else if (role === 'parent') {
      url.searchParams.set('next', getConfig().PARENT_DASHBOARD_URL);
    } else {
      url.searchParams.delete('next');
    }

    window.history.replaceState({}, '', url.toString());
  };

  return (
    <div className="mb-4 role-redirect-widget">
      <p className="mb-2" style={roleRedirectLabelStyle}>Login as:</p>
      <ButtonGroup className="w-100">
        <Button
          variant={selectedRole === 'learner' ? 'primary' : 'outline-primary'}
          onClick={() => handleRoleChange('learner')}
          size="sm"
        >
          Learner
        </Button>
        <Button
          variant={selectedRole === 'instructor' ? 'primary' : 'outline-primary'}
          onClick={() => handleRoleChange('instructor')}
          size="sm"
        >
          Instructor
        </Button>
        <Button
          variant={selectedRole === 'parent' ? 'primary' : 'outline-primary'}
          onClick={() => handleRoleChange('parent')}
          size="sm"
        >
          Parent
        </Button>
      </ButtonGroup>
    </div>
  );
};

    const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
    addPlugins(config, 'org.openedx.frontend.layout.header_desktop_secondary_menu.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'notification-drawer-widget',
        priority: 10,
        type: DIRECT_PLUGIN,
        RenderWidget: NotificationsTray,
      },
    }
]);
    if (process.env.APP_ID == 'admin-console') {
    }
    if (process.env.APP_ID == 'authn') {
      addPlugins(config, 'org.openedx.frontend.authn.login_component.v1', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'role_redirect_plugin',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: RoleRedirectWidget,
            },
        },
        ]);
    }
    if (process.env.APP_ID == 'authoring') {


      const { CourseOutlineSidebar, UnitPageSidebar, SidebarToggleWrapper, CourseHeaderButton, UnitActionsButton, SubSectionAnalyticsButton } = await import("@openedx/frontend-plugin-aspects");


      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseOutlineSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_sidebar.v1', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'course-unit-sidebar',
                    priority: 1,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitPageSidebar,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_sidebar.v2', [
            {
                op: PLUGIN_OPERATIONS.Wrap,
                widgetId: 'default_contents',
                wrapper: SidebarToggleWrapper,
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_unit_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'unit-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_header_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'outline-header-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: CourseHeaderButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_unit_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: UnitActionsButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.authoring.course_outline_subsection_card_extra_actions.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'units-action-aspects-button',
                    priority: 60,
                    type: DIRECT_PLUGIN,
                    RenderWidget: SubSectionAnalyticsButton,
                },
            }]);
      addPlugins(config, 'org.openedx.frontend.layout.studio_footer.v1', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'custom_footer',
                type: DIRECT_PLUGIN,
                priority: 1,
                RenderWidget: HyperStudioFooter,
            },
        }
        ]);
    }
    if (process.env.APP_ID == 'account') {
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
const fpf = await import('@openedx/frontend-plugin-framework');
const React = (await import('react')).default;
const { getConfig } = await import('@edx/frontend-platform');
const { getAuthenticatedHttpClient } = await import('@edx/frontend-platform/auth');
const { Card, Stack, Badge, Button, Hyperlink } = await import('@openedx/paragon');

const LIVE_SESSIONS_CATALOGUE_PATH = '/api/live-sessions/v1/catalogue/';
const LIVE_SESSIONS_SLOT_ID = 'org.openedx.frontend.learner_dashboard.widget_sidebar.v1';
const LIVE_SESSIONS_MAX_ITEMS = 5; // cap the sidebar; "See all" covers the rest

const lsFormatStartsIn = (seconds) => {
  if (!seconds || seconds <= 0) { return 'Starting soon'; }
  const days = Math.floor(seconds / 86400);
  if (days >= 1) { return `in ${days}d`; }
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) { return `in ${hours}h`; }
  const minutes = Math.max(1, Math.floor(seconds / 60));
  return `in ${minutes}m`;
};

const lsFormatWhen = (iso, timeZone) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timeZone || undefined,
    }).format(new Date(iso));
  } catch (e) {
    return '';
  }
};

const LiveSessionsSidebarWidget = () => {
  const [items, setItems] = React.useState(null); // null = loading, [] = empty

  React.useEffect(() => {
    let cancelled = false;
    const client = getAuthenticatedHttpClient();
    const base = getConfig().LMS_BASE_URL;
    const url = (scope) => `${base}${LIVE_SESSIONS_CATALOGUE_PATH}?scope=${scope}&page_size=${LIVE_SESSIONS_MAX_ITEMS}`;

    Promise.all([client.get(url('live')), client.get(url('upcoming'))])
      .then(([live, upcoming]) => {
        if (cancelled) { return; }
        // Live first, then upcoming, capped at the max the sidebar shows.
        const results = [
          ...((live.data && live.data.results) || []),
          ...((upcoming.data && upcoming.data.results) || []),
        ].slice(0, LIVE_SESSIONS_MAX_ITEMS);
        setItems(results);
      })
      .catch(() => { if (!cancelled) { setItems([]); } });

    return () => { cancelled = true; };
  }, []);

  // Only bail while still loading; once loaded, always render the heading and
  // an explicit empty message so the learner knows the feature is there.
  if (items === null) { return null; }

  const liveSessionsBase = getConfig().LIVE_SESSIONS_MICROFRONTEND_URL || '';

  return (
    <div className="live-sessions-widget mb-4">
      {/* The sidebar column pushes its first child down by spacer-5-5 to align
          widgets with the course cards; we want our heading to line up with the
          "My Courses" h2 instead, so cancel that offset (and the column's own
          padding-top: spacer-2) for this slot. */}
      <style>{`.sidebar-column.not-collapsed > :first-child { margin-top: calc(var(--pgn-spacing-spacer-2) * -1) !important; }`}</style>
      <div className="course-list-heading-container">
        <h2 className="course-list-title">Live sessions</h2>
      </div>
      <Card>
        <Card.Section>
          {items.length === 0 && (
            <p className="small text-gray-500 mb-0">
              No ongoing or upcoming live sessions for you.
            </p>
          )}
          <Stack gap={3}>
          {items.map((occ) => (
            <div key={occ.id} className="pb-2 border-bottom">
              <div className="d-flex justify-content-between align-items-start">
                <span className="font-weight-bold pr-2">{occ.title}</span>
                {occ.state === 'live'
                  ? <Badge variant="danger">Live</Badge>
                  : <Badge variant="light">{lsFormatStartsIn(occ.starts_in_seconds)}</Badge>}
              </div>
              {occ.host && occ.host.name && (
                <div className="small text-gray-500 mt-1">{occ.host.name}</div>
              )}
              {occ.actions && occ.actions.join ? (
                <Button
                  as="a"
                  href={`${liveSessionsBase}/sessions/${occ.session_id}`}
                  variant="brand"
                  size="sm"
                  className="mt-2"
                >
                  Join now
                </Button>
              ) : (
                <div className="small text-gray-500 mt-1">
                  {lsFormatWhen(occ.starts_at, occ.timezone)}
                </div>
              )}
            </div>
          ))}
          </Stack>
          {liveSessionsBase && (
            <Hyperlink destination={liveSessionsBase} className="small d-inline-block mt-3">
              See all live sessions
            </Hyperlink>
          )}
        </Card.Section>
      </Card>
    </div>
  );
};

// Additive insert; keepDefault preserves the default challenge widget.
config.pluginSlots[LIVE_SESSIONS_SLOT_ID] = config.pluginSlots[LIVE_SESSIONS_SLOT_ID]
  || { keepDefault: true, plugins: [] };
config.pluginSlots[LIVE_SESSIONS_SLOT_ID].plugins.push({
  op: fpf.PLUGIN_OPERATIONS.Insert,
  widget: {
    id: 'live_sessions_sidebar',
    type: fpf.DIRECT_PLUGIN,
    priority: 1,
    RenderWidget: LiveSessionsSidebarWidget,
  },
});
      addPlugins(config, 'org.openedx.frontend.layout.header_desktop_secondary_menu.v1', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'custom_secondary_menu_component',
                type: DIRECT_PLUGIN,
                RenderWidget: ParentDashboardLink,
            },
        },
        ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'learning_help_slot', [ 
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'theme_switch_button',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
    }
    if (process.env.APP_ID == 'catalog') {
      addPlugins(config, 'org.openedx.frontend.catalog.home_page.overlay_html', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        },
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'custom_home_page_overlay_html_component',
                type: DIRECT_PLUGIN,
                RenderWidget: CatalogHomeOverlay,
            },
        }
        ]);
    }
    if (process.env.APP_ID == 'parent') {
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
    }
    if (process.env.APP_ID == 'live-sessions') {
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;